import { useEffect, useState, useContext } from "react";
import {
  faCreditCard,
  faCircleCheck,
  faCirclePlay,
  faHeartPulse,
  faAppleWhole,
  faChartLine,
  faCrown,
  faEnvelope,
  faLock,
  faRotate,
  faShieldHalved,
} from "@fortawesome/free-solid-svg-icons";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import useAxiosPrivate from "../hooks/useAxiosPrivate";
import { toast } from "sonner";
import { capitaliseFirstLetter } from "../utils";
import AuthContext from "../context/AuthProvider";
import iosDownloadImage from "../img/IOS download image.svg";
import androidDownloadImage from "../img/android download image.png";
import getTheAppQrCode from "../img/get-the-app-qr.png";
import getTheAppTestQrCode from "../img/get-the-app-qr-test.png";

const appQrCode =
  process.env.REACT_APP_ENV === "production"
    ? getTheAppQrCode
    : getTheAppTestQrCode;

const Membership = () => {
  const { auth } = useContext(AuthContext);
  const subscription = auth?.user?.membership;
  const hadSubscription = auth?.user?.had_subscription; //if the user has had a previous subscription
  const axios = useAxiosPrivate();
  const [plans, setPlans] = useState([]);
  const [loading, setLoading] = useState(false);
  const [upgrading, setUpgrading] = useState(false);
  const [downgradePlan, setDowngradePlan] = useState(null);
  const [openFaq, setOpenFaq] = useState(null);
  const [billingPeriod, setBillingPeriod] = useState("monthly");

  const featureIcons = {
    "heart-pulse": faHeartPulse,
    "circle-play": faCirclePlay,
    "chart-line": faChartLine,
  };

  const hasActiveSubscription = (() => {
    if (!subscription) return false;
    switch (subscription.stripe_status) {
      case "active":
      case "paused":
      case "canceling":
      case "trialing":
        return true;
      case "canceled":
        return subscription.ends_at
          ? new Date(subscription.ends_at) > new Date()
          : false;
      default:
        return false;
    }
  })();

  const currentPlan = hasActiveSubscription ? subscription?.plan : null;
  const currentFeatures = currentPlan?.features ?? [];

  const statusClasses = {
    active: "bg-green-100 text-green-700",
    canceling: "bg-yellow-100 text-yellow-700",
    trialing: "bg-yellow-100 text-yellow-700",
    canceled: "bg-red-100 text-red-700",
    paused: "bg-orange-100 text-orange-700",
  };

  useEffect(() => {
    const fetchPlans = async () => {
      try {
        setLoading(true);
        const { data } = await axios.get("/stripe/plans");
        const raw = data[0]?.value;
        if (!raw) return;
        const parsed = typeof raw === "string" ? JSON.parse(raw) : raw;
        setPlans(Object.values(parsed || {}));
      } catch (e) {
        toast.error("Unable to load subscription plans.");
      } finally {
        setLoading(false);
      }
    };
    fetchPlans();
  }, []);


  useEffect(() => {
    //check their billing period
    if (hasActiveSubscription) {
      setBillingPeriod(subscription.plan.billing)
    }
  }, [])


  const isCurrentPlan = (plan) =>
    plan?.slug?.toLowerCase() === currentPlan?.slug?.toLowerCase();
  const getPlanDirection = (plan) => {
    if (!currentPlan) return "upgrade";
    if (plan.price > currentPlan.price) return "upgrade";
    if (plan.price < currentPlan.price) return "downgrade";
    return "current";
  };

  const upgradePlans = plans.filter(
    (plan) => !hasActiveSubscription || !isCurrentPlan(plan),
  );

  const upgradePlan = async (planName) => {
    try {
      setUpgrading(true);
      const { data } = await axios.post("/stripe/subscription/update", {
        plan: planName.toLowerCase(),
        billing_period: billingPeriod,
        mobile: localStorage.getItem('from-mobile')
      });
      if (data.result.url) {
        localStorage.removeItem('from-mobile')
        window.location.href = data.result.url;
        return;
      }
      toast.success("Membership updated successfully!");
      window.location.reload();
    } catch (e) {
      toast.error("Failed to update subscription.");
    } finally {
      setUpgrading(false);
    }
  };

  const openBillingPortal = async () => {
    try {
      const response = await axios.post("/stripe/portal", {
        returnUrl: window.location.origin,
      });
      window.location.href = response.data.result.url;
    } catch (error) {
      toast.error("Could not open billing portal");
    }
  };
  const defaultFeatureList = [
    {
      title: "Personalised support",
      description: "Tailored tools and guidance for your wellbeing.",
      icon: "heart-pulse",
      colour: "#E9D9F8",
    },
    {
      title: "Expert backed",
      description: "Access professional services and resources.",
      icon: "shield",
      colour: "#E9D9F8",
    },
    {
      title: "Track & improve",
      description: "Monitor your progress and build healthy habits.",
      icon: "chart-line",
      colour: "#E9D9F8",
    },
  ];

  const membershipFeatures = plans?.[0]?.features?.length
    ? plans[0].features
    : defaultFeatureList;

  return (
    <div className="">
      <div className="">
        <header className="mb-7 max-w-[80%] px-1 pt-1 sm:px-2">
          <div>
            <h1 className="text-[1.75rem] font-bold leading-none text-[#3b214c] sm:text-[2rem]">
              SWB+ Membership
            </h1>
            {!hasActiveSubscription && (
              <p className="mt-2 text-base leading-5 text-[#5e6272]">
                Enhance your wellbeing journey with SWB+. Enjoy premium tools,
                expert support, and personalised insights to help you feel your
                best. All while continuing to enjoy all the benefits of the
                existing School Wellbeing service purchased for them.
              </p>
            )}
          </div>

          <div className="mt-8 grid gap-6 sm:grid-cols-3 sm:gap-4">
            {defaultFeatureList.map((feature, index) => (
              <div key={feature.title} className="flex items-start gap-3">
                <div className="flex h-12 w-12 shrink-0 items-center justify-center rounded-lg border border-[#e4d4ee] bg-[#f7f1fb] text-[#67428d]">
                  <FontAwesomeIcon
                    icon={
                      index === 1
                        ? faShieldHalved
                        : featureIcons[feature.icon] || faHeartPulse
                    }
                    className="text-xl"
                  />
                </div>
                <div className="">
                  <h2 className="font-bold text-[#3b214c]">{feature.title}</h2>
                  <p className="mt-1 text-sm leading-5 text-[#5e6272]">
                    {feature.description}
                  </p>
                </div>
              </div>
            ))}
          </div>
        </header>

        <section className="mt-8 rounded-lg border border-[#e5dfeb] bg-white p-4 shadow-[0_2px_9px_rgba(57,34,72,0.03)]">
          <h2 className="mb-5 font-bold text-xl">
            {hasActiveSubscription ? "Manage" : "Updgrade"} your membership
            today!
          </h2>
          <div className="grid gap-5 lg:grid-cols-[1.1fr_1.25fr]">
            <div className="rounded-lg border border-[#a77bcd] bg-[#fffdff] p-4 shadow-[0_1px_4px_rgba(78,43,105,0.08)]">
              <div className="mb-3 flex items-center justify-between gap-3">
                <h3 className="text-xl font-bold leading-none text-[#3b214c]">
                  SWB+
                </h3>
                {hasActiveSubscription ? (
                  <div className="rounded-full border border-[#d5c5e8] bg-[#f7f1fb] px-3 py-1 text-xs font-semibold text-[#7955a4]">
                    {subscription?.plan?.billing === "annual" ? "Annual" : "Monthly"}
                  </div>
                ) : (
                  <div className="flex items-center gap-0.5 rounded-full border border-[#d5c5e8] bg-[#f7f1fb] p-0.5 text-xs font-semibold">
                    <button
                      type="button"
                      onClick={() => setBillingPeriod("monthly")}
                      className={`rounded-full px-3 py-1 transition ${billingPeriod === "monthly"
                        ? "bg-[#7955a4] text-white shadow-sm"
                        : "text-[#7955a4] hover:text-[#5c3d82]"
                        }`}
                    >
                      Monthly
                    </button>

                    <button
                      type="button"
                      onClick={() => setBillingPeriod("annual")}
                      className={`rounded-full px-3 py-1 transition ${billingPeriod === "annual"
                        ? "bg-[#7955a4] text-white shadow-sm"
                        : "text-[#7955a4] hover:text-[#5c3d82]"
                        }`}
                    >
                      Annual
                    </button>
                  </div>
                )}
              </div>
              <p className="min-h-[40px] text-sm leading-4 text-[#5e6272]">
                Upgrade your membership to SWB+ to unlock even more tools,
                resources and support for your wellbeing journey. Enjoy
                personalised insights, more health resources and expert
                guidance.
              </p>
              <div className="mt-2">
                <div className="text-3xl font-bold leading-none text-[#3b214c]">
                  {billingPeriod === "monthly" ? (
                    <>
                      £2.50
                      <span className="ml-1 align-middle text-sm font-medium text-slate-500">
                        / month
                      </span>
                    </>
                  ) : (
                    <>
                      £25
                      <span className="ml-1 align-middle text-sm font-medium text-slate-500">
                        / year
                      </span>
                    </>
                  )}
                </div>
                {!hasActiveSubscription && billingPeriod === "monthly" && (
                  <p className="mt-2 inline-flex items-center rounded-md bg-[#eef9f1] px-2 py-1 text-xs font-semibold text-[#267443]">
                    First month free when you sign up before 31st December 2026
                  </p>
                )}
                {!hasActiveSubscription && billingPeriod === "annual" && (
                  <p className="mt-2 inline-flex items-center gap-1 rounded-md bg-[#eef9f1] px-2 py-1 text-xs font-semibold text-[#267443]">
                    Best value — pay for 10 months, enjoy 13 (includes 1 month
                    free)
                  </p>
                )}
              </div>
              <button
                type="button"
                onClick={() =>
                  hasActiveSubscription
                    ? openBillingPortal()
                    : upgradePlan(plans?.[0]?.name || "SWB+")
                }
                disabled={upgrading || loading}
                className="mt-4 w-full rounded-md bg-[#7955a4] px-3 py-2.5 text-base font-semibold text-white transition hover:bg-[#66448d] disabled:cursor-not-allowed disabled:opacity-60"
              >
                {
                  upgrading
                    ? "Updating membership..."
                    : hasActiveSubscription
                      ? "Manage membership"
                      : !hadSubscription
                        ? billingPeriod === "annual"
                          ? "Claim your free month"
                          : "Try free for a month"
                        : "Upgrade to SWB+ today"
                }
              </button>
              <div className="mt-3 flex items-center gap-2 text-[11px] text-[#737384]">
                <FontAwesomeIcon
                  icon={faCircleCheck}
                  className="text-[#48b66b]"
                />{" "}
                Cancel anytime
                <FontAwesomeIcon
                  icon={faLock}
                  className="ml-1 text-[#48b66b]"
                />{" "}
                Secure payment
              </div>
            </div>

            <div className="pb-2">
              <h3 className="mb-4 text-base font-bold text-[#3b214c]">
                What&apos;s included:
              </h3>
              <ul className="space-y-3 text-sm leading-4 text-[#5e6272]">
                {[
                  [
                    "Thousands of On-Demand Virtual Classes",
                    "Enjoy flexible fitness, mindfulness and wellbeing classes whenever it suits you.",
                  ],
                  [
                    "Connected Health & Wellbeing Insights",
                    "Understand your wellbeing with personalised insights and simple habit tracking.",
                  ],
                  [
                    "Premium Health Resources and Support",
                    "Explore expert articles, practical guides and self-help tools for everyday support.",
                  ],
                  [
                    "Priority access to new and exclusive app features",
                    "Be among the first to discover and use new SWB+ features.",
                  ],
                ].map(([title, description]) => (
                  <li key={title} className="flex gap-2">
                    <FontAwesomeIcon
                      icon={faCircleCheck}
                      className="mt-0.5 text-[#49b96e]"
                    />
                    <span>
                      <strong className="font-bold text-[#493354]">
                        {title}
                      </strong>
                      <br />
                      {description}
                    </span>
                  </li>
                ))}
              </ul>
            </div>
          </div>
        </section>

        {!hasActiveSubscription && (
          <div className="mt-8 grid gap-5 md:grid-cols-2">
            <div className="rounded-lg border border-[#e5dfeb] bg-[#faf6fd] p-4 shadow-[0_2px_9px_rgba(57,34,72,0.03)]">
              <h3 className="mb-4 flex items-center gap-2 text-base font-bold text-[#3b214c]">
                <FontAwesomeIcon icon={faCrown} className="text-[#e1ad22]" />{" "}
                Why upgrade?
              </h3>
              <div className="space-y-3">
                {[
                  [
                    "More ways to stay active",
                    "Choose from on-demand fitness, mindfulness and wellbeing classes when they suit your routine.",
                  ],
                  [
                    "A clearer view of your wellbeing",
                    "Use your check-ins and activity to spot patterns and build habits that work for you.",
                  ],
                  [
                    "Helpful resources in one place",
                    "Find practical guides, expert content and self-help tools whenever you need them.",
                  ],
                ].map(([title, description]) => (
                  <div key={title} className="flex gap-2.5">
                    <span className="flex h-6 w-6 shrink-0 items-center justify-center rounded-md bg-[#f0e6f8] text-sm text-[#725096]">
                      ✦
                    </span>
                    <p className="text-sm leading-4 text-[#686878]">
                      <strong className="block text-[#493354]">{title}</strong>
                      {description}
                    </p>
                  </div>
                ))}
              </div>
            </div>

            <div className="rounded-lg border border-[#e5dfeb] bg-white p-4 shadow-[0_2px_9px_rgba(57,34,72,0.03)]">
              <h3 className="text-base font-bold text-[#3b214c]">
                Trusted by schools and thousands of staff
              </h3>
              <p className="mt-1 max-w-[430px] text-sm leading-4 text-[#656879]">
                SWB+ is part of the School Wellbeing platform, designed with
                your privacy and security in mind.
              </p>

              <div className="mt-4 grid gap-3 sm:grid-cols-3">
                {[
                  [
                    faCreditCard,
                    "Secure payments",
                    "Encrypted and PCI compliant",
                  ],
                  [
                    faLock,
                    "Your data is safe",
                    "We never share your personal information",
                  ],
                  [faRotate, "Cancel anytime", "No contracts, no hassle"],
                ].map(([icon, title, text]) => (
                  <div key={title} className="flex gap-2">
                    <div className="flex h-7 w-7 shrink-0 items-center justify-center rounded-md bg-[#f3ebf9] text-[#7652a0]">
                      <FontAwesomeIcon icon={icon} className="text-sm" />
                    </div>
                    <div>
                      <div className="text-sm font-bold text-[#493354]">
                        {title}
                      </div>
                      <div className="mt-0.5 text-[11px] leading-4 text-[#777686]">
                        {text}
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          </div>
        )}

        <div className="mt-8 mb-4 flex flex-col gap-4 rounded-lg border border-[#e4d8ef] bg-[#f4ecfa] px-5 py-4 md:flex-row md:items-center md:justify-between">
          <div className="flex items-center gap-4">
            <img
              src={appQrCode}
              alt="Scan to get the School Wellbeing app"
              className="hidden h-20 w-20 shrink-0 object-contain sm:block"
            />
            <div>
              <h3 className="text-base font-bold text-[#3b214c]">
                Get even more with the mobile app
              </h3>
              <p className="mt-1 text-sm leading-4 text-[#656879]">
                Make the most of your membership with the SWB app. Track your
                wellbeing, access services, and get support anytime, anywhere.
              </p>
            </div>
          </div>

          <div className="flex flex-col items-center gap-2 md:items-end">
            <div className="flex flex-wrap items-center justify-center gap-2">
              <a
                href="#app-store"
                className="flex h-11 w-36 shrink-0 items-center justify-center"
              >
                <img
                  src={iosDownloadImage}
                  alt="Download on the App Store"
                  className="h-full w-full object-contain"
                  href="https://apps.apple.com/gb/app/school-wellbeing/id1663791750"
                  target="_blank"
                  rel="noopener noreferrer"
                />
              </a>
              <a
                href="#google-play"
                className="flex h-11 w-36 shrink-0 items-center justify-center"
              >
                <img
                  src={androidDownloadImage}
                  alt="Get it on Google Play"
                  className="h-full w-full object-contain"
                  href="https://play.google.com/store/apps/details?id=com.schoolwellbeing.mobileapp&gl=GB"
                  target="_blank"
                  rel="noopener noreferrer"
                />
              </a>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default Membership;
